'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useDecisions } from '../../hooks';
import { hasConsentGroup } from '../utils';

export const useSquadupModalScript = () => {
    const isOneTrust = useDecisions('onetrust');
    const scriptLoaded = useRef(false);
    const scriptEl = useRef<HTMLScriptElement | null>(null);
    const scriptLoadingPromise = useRef<Promise<void> | null>(null);

    const loadSquadupScript = useCallback(() => {
        if (scriptLoaded.current) {
            return Promise.resolve();
        }

        if (scriptLoadingPromise.current) {
            return scriptLoadingPromise.current;
        }

        if (isOneTrust && !hasConsentGroup({ group: 'C0003', groups: window.OnetrustActiveGroups })) {
            return Promise.reject(new Error('No consent for C0003 category'));
        }

        scriptLoadingPromise.current = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://embed.squadup.com/main-v2.min.js';
            script.async = true;

            script.onload = () => {
                scriptLoaded.current = true;
                scriptEl.current = script;
                scriptLoadingPromise.current = null;
                resolve();
            };

            script.onerror = () => {
                scriptLoadingPromise.current = null;
                reject(new Error('Failed to load SquadUp script'));
            };

            document.body.appendChild(script);
        });

        return scriptLoadingPromise.current;
    }, [isOneTrust]);

    useEffect(() => {
        return () => {
            if (scriptEl.current) {
                document.body.removeChild(scriptEl.current);
                scriptEl.current = null;
                scriptLoaded.current = false;
            }
            scriptLoadingPromise.current = null;
        };
    }, []);

    return { loadSquadupScript, isScriptLoaded: scriptLoaded };
};
